import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Settings as SettingsIcon, Users, ShieldCheck, BookOpen, Plus, Edit, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

const Settings = ({ user }) => {
  const navigate = useNavigate();
  const [darkMode, setDarkMode] = useState(false);
  const [language, setLanguage] = useState("english");
  const [themeColor, setThemeColor] = useState("#1E624A");

  // Subjects state
  const [subjects, setSubjects] = useState([]);
  const [subjectsLoading, setSubjectsLoading] = useState(false);
  const [newSubjectName, setNewSubjectName] = useState("");
  const [editingSubject, setEditingSubject] = useState(null);
  const [editSubjectName, setEditSubjectName] = useState("");
  const [isSubjectDialogOpen, setIsSubjectDialogOpen] = useState(false);

  // Check if user has settings permission
  const hasSettingsPermission = user?.role === "super_admin" || 
    user?.role === "admin" || 
    (user?.permissions && user?.permissions?.settings);

  // User management requires super admin only
  const hasUserManagementPermission = user?.role === "super_admin";

  // Subjects CRUD is admin-only
  const canManageSubjects =
    user?.role === "super_admin" || user?.role === "admin";

  useEffect(() => {
    // Load saved settings from localStorage
    const savedDarkMode = localStorage.getItem("darkMode") === "true";
    const savedLanguage = localStorage.getItem("language") || "english";
    const savedThemeColor = localStorage.getItem("themeColor") || "#1E624A";

    setDarkMode(savedDarkMode);
    setLanguage(savedLanguage);
    setThemeColor(savedThemeColor);

    // Apply dark mode
    if (savedDarkMode) {
      document.body.classList.add("dark");
    }

    if (canManageSubjects) {
      fetchSubjects();
    }
  }, []);

  const fetchSubjects = async () => {
    setSubjectsLoading(true);
    try {
      const res = await axios.get(`${API}/subjects`);
      setSubjects(res.data || []);
    } catch (error) {
      toast.error("Failed to load subjects");
    } finally {
      setSubjectsLoading(false);
    }
  };

  const handleAddSubject = async (e) => {
    e.preventDefault();
    const name = newSubjectName.trim();
    if (!name) return;
    try {
      await axios.post(`${API}/subjects`, { name });
      toast.success(`✅ Subject "${name}" added`);
      setNewSubjectName("");
      fetchSubjects();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add subject");
    }
  };

  const openEditSubject = (subject) => {
    setEditingSubject(subject);
    setEditSubjectName(subject.name);
    setIsSubjectDialogOpen(true);
  };

  const handleUpdateSubject = async (e) => {
    e.preventDefault();
    if (!editingSubject) return;
    const name = editSubjectName.trim();
    if (!name) return;
    try {
      await axios.put(`${API}/subjects/${editingSubject.id}`, { name });
      toast.success("✅ Subject updated");
      setIsSubjectDialogOpen(false);
      setEditingSubject(null);
      fetchSubjects();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update subject");
    }
  };

  const handleDeleteSubject = async (subject) => {
    if (!window.confirm(`Delete subject "${subject.name}"? Existing assessments already using this name are NOT affected.`)) {
      return;
    }
    try {
      await axios.delete(`${API}/subjects/${subject.id}`);
      toast.success("Subject deleted");
      fetchSubjects();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete subject");
    }
  };

  const handleDarkModeToggle = (checked) => {
    setDarkMode(checked);
    localStorage.setItem("darkMode", checked.toString());
    if (checked) {
      document.body.classList.add("dark");
    } else {
      document.body.classList.remove("dark");
    }
  };

  const handleLanguageChange = (value) => {
    setLanguage(value);
    localStorage.setItem("language", value);
  };

  const handleThemeColorChange = (value) => {
    setThemeColor(value);
    localStorage.setItem("themeColor", value);
    // Apply theme color to root CSS variable (you'd need to add this to your CSS)
    document.documentElement.style.setProperty("--primary-color", value);
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h2 className="text-2xl font-bold text-[#1E624A]">Settings</h2>
        <p className="text-sm text-gray-600">Dejinta / إعدادات</p>
      </div>

      {/* User Management Section - Only for super admins */}
      {hasUserManagementPermission && (
        <div className="bg-white rounded-xl p-6 shadow-md">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <ShieldCheck size={20} className="text-[#1E624A]" />
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  User Management
                </h3>
                <p className="text-sm text-gray-600">Maamulka Isticmaalayaasha</p>
                <p className="text-xs text-gray-500 mt-1">
                  Manage system users, roles, and permissions
                </p>
              </div>
            </div>
            <Button
              onClick={() => navigate("/user-management")}
              className="bg-[#1E624A] hover:bg-[#164935]"
              data-testid="user-management-btn"
            >
              <Users size={16} className="mr-2" />
              Manage Users
            </Button>
          </div>
        </div>
      )}

      {/* Subjects Management - Admin & Super Admin */}
      {canManageSubjects && (
        <div className="bg-white rounded-xl p-6 shadow-md" data-testid="subjects-management-section">
          <div className="flex items-center gap-3 mb-4">
            <BookOpen size={20} className="text-[#1E624A]" />
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Subjects Management
              </h3>
              <p className="text-sm text-gray-600">Maaraynta Maaddooyinka</p>
              <p className="text-xs text-gray-500 mt-1">
                Preset subject list used across the system when creating assessments. Keeps naming consistent for reports.
              </p>
            </div>
          </div>

          {/* Add form */}
          <form onSubmit={handleAddSubject} className="flex flex-col sm:flex-row gap-2 mb-4">
            <Input
              data-testid="new-subject-input"
              placeholder="e.g., Quran, Tajweed, Arabic, Islamic Studies"
              value={newSubjectName}
              onChange={(e) => setNewSubjectName(e.target.value)}
              className="flex-1"
            />
            <Button
              type="submit"
              data-testid="add-subject-btn"
              disabled={!newSubjectName.trim()}
              className="bg-[#1E624A] hover:bg-[#164935]"
            >
              <Plus size={16} className="mr-1" />
              Add Subject
            </Button>
          </form>

          {/* List */}
          {subjectsLoading ? (
            <p className="text-sm text-gray-500">Loading...</p>
          ) : subjects.length === 0 ? (
            <div className="text-sm text-gray-500 bg-gray-50 rounded-lg p-4 text-center">
              No subjects yet. Add your first subject above to enable the assessment dropdown.
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {subjects.map((s) => (
                <div
                  key={s.id}
                  data-testid={`subject-chip-${s.id}`}
                  className="inline-flex items-center gap-2 bg-[#E4C987]/20 border border-[#E4C987]/50 rounded-full px-3 py-1.5 text-sm"
                >
                  <span className="font-medium text-gray-800">{s.name}</span>
                  <button
                    type="button"
                    data-testid={`edit-subject-${s.id}-btn`}
                    onClick={() => openEditSubject(s)}
                    className="text-[#1E624A] hover:text-[#164935]"
                    aria-label="Edit subject"
                  >
                    <Edit size={14} />
                  </button>
                  <button
                    type="button"
                    data-testid={`delete-subject-${s.id}-btn`}
                    onClick={() => handleDeleteSubject(s)}
                    className="text-red-500 hover:text-red-700"
                    aria-label="Delete subject"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Edit dialog */}
          <Dialog open={isSubjectDialogOpen} onOpenChange={setIsSubjectDialogOpen}>
            <DialogContent data-testid="edit-subject-dialog">
              <DialogHeader>
                <DialogTitle>Edit Subject</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleUpdateSubject} className="space-y-4">
                <div>
                  <Label htmlFor="edit-subject-name">Subject name</Label>
                  <Input
                    id="edit-subject-name"
                    data-testid="edit-subject-input"
                    value={editSubjectName}
                    onChange={(e) => setEditSubjectName(e.target.value)}
                    required
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsSubjectDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    data-testid="save-subject-btn"
                    className="bg-[#1E624A] hover:bg-[#164935]"
                  >
                    Save
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      )}

      <div className="bg-white rounded-xl p-6 shadow-md space-y-6">
        {/* Language Settings */}
        <div className="pb-6 border-b border-gray-200">
          <div className="flex items-center gap-3 mb-4">
            <SettingsIcon size={20} className="text-[#1E624A]" />
            <h3 className="text-lg font-semibold text-gray-900">
              Language / Luuqadda
            </h3>
          </div>
          <div className="max-w-xs">
            <Label htmlFor="language">Select Language</Label>
            <Select value={language} onValueChange={handleLanguageChange}>
              <SelectTrigger id="language" data-testid="language-select" className="mt-2">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="english">English</SelectItem>
                <SelectItem value="somali">Somali (Soomaali)</SelectItem>
                <SelectItem value="arabic">Arabic (العربية)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500 mt-2">
              Note: Full translation coming soon. Currently showing key labels in Somali.
            </p>
          </div>
        </div>

        {/* Theme Color */}
        <div className="pb-6 border-b border-gray-200">
          <div className="flex items-center gap-3 mb-4">
            <SettingsIcon size={20} className="text-[#1E624A]" />
            <h3 className="text-lg font-semibold text-gray-900">
              Theme Color / Midabka
            </h3>
          </div>
          <div className="max-w-xs">
            <Label htmlFor="theme-color">Primary Color</Label>
            <Select value={themeColor} onValueChange={handleThemeColorChange}>
              <SelectTrigger id="theme-color" data-testid="theme-color-select" className="mt-2">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="#1E624A">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-[#1E624A]"></div>
                    Deep Green (Default)
                  </div>
                </SelectItem>
                <SelectItem value="#2C5F2D">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-[#2C5F2D]"></div>
                    Forest Green
                  </div>
                </SelectItem>
                <SelectItem value="#004E89">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-[#004E89]"></div>
                    Navy Blue
                  </div>
                </SelectItem>
                <SelectItem value="#6B4423">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-[#6B4423]"></div>
                    Brown
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Dark Mode (Future Feature) */}
        <div>
          <div className="flex items-center gap-3 mb-4">
            <SettingsIcon size={20} className="text-[#1E624A]" />
            <h3 className="text-lg font-semibold text-gray-900">
              Dark Mode / Qaabka Mugdiga
            </h3>
          </div>
          <div className="flex items-center justify-between max-w-xs">
            <Label htmlFor="dark-mode" className="cursor-pointer">
              Enable Dark Mode
            </Label>
            <Switch
              id="dark-mode"
              data-testid="dark-mode-toggle"
              checked={darkMode}
              onCheckedChange={handleDarkModeToggle}
            />
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Note: Dark mode is coming soon.
          </p>
        </div>
      </div>

      {/* System Info */}
      <div className="bg-white rounded-xl p-6 shadow-md">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          System Information
        </h3>
        <div className="space-y-2 text-sm text-gray-600">
          <p>
            <span className="font-medium">System Name:</span> Taaj Al Waqaar
            Madrasa Management System
          </p>
          <p>
            <span className="font-medium">Version:</span> 1.0.0
          </p>
          <p>
            <span className="font-medium">Last Updated:</span> January 2025
          </p>
        </div>
      </div>
    </div>
  );
};

export default Settings;
